import React, { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  Square,
  RotateCcw,
  Users,
  Shield,
  Play,
  X,
  Check,
  Info,
  Zap,
  Watch,
  Power,
  Mic,
  Video,
  Lock,
  Heart,
  Gamepad2,
} from 'lucide-react';
import TurnBasedDuelModal from './TurnBasedDuelModal';
import InvestigationReportModal from './InvestigationReportModal';
import TileDetailModal from './TileDetailModal';
import { TILES_METADATA } from './GameSetupModal';
import { MAX_AGILITY_CAP, MAX_CHARISMA_CAP } from '../../utils/xpProgression';
import useDragToScroll from '../../hooks/useDragToScroll';
import sfx from '../../utils/soundEffects';

// GRILLE URBAINE ÉTENDUE ET DÉZOOMÉE (1080 x 600)
export const STREET_SEGMENTS = [
  // Axes Horizontaux
  { id: 'h1', name: 'Rue de Rivoli', y: 80, xMin: 100, xMax: 1010, orientation: 'H' },
  { id: 'h2', name: 'Rue de la Paix', y: 170, xMin: 100, xMax: 1010, orientation: 'H' },
  { id: 'h3', name: 'Avenue Victor Hugo', y: 260, xMin: 100, xMax: 1010, orientation: 'H' },
  { id: 'h4', name: 'Boulevard Saint-Michel', y: 350, xMin: 100, xMax: 1010, orientation: 'H' },
  { id: 'h5', name: 'Avenue des Champs-Élysées', y: 440, xMin: 100, xMax: 1010, orientation: 'H' },
  { id: 'h6', name: 'Rue Gambetta', y: 530, xMin: 100, xMax: 1010, orientation: 'H' },

  // Axes Verticaux
  { id: 'v1', name: 'Rue de l’École', x: 100, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v2', name: 'Rue du Commerce', x: 260, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v3', name: 'Boulevard Haussmann', x: 420, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v4', name: 'Allée des Lilas', x: 580, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v5', name: 'Rue de la République', x: 740, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v6', name: 'Rue de Paris', x: 900, yMin: 80, yMax: 530, orientation: 'V' },
  { id: 'v7', name: 'Allée du Parc', x: 1010, yMin: 80, yMax: 530, orientation: 'V' },
];

const H_STREETS = [80, 170, 260, 350, 440, 530];
const V_STREETS = [100, 260, 420, 580, 740, 900, 1010];

export const INTERSECTIONS = [];
H_STREETS.forEach(y => {
  V_STREETS.forEach(x => {
    INTERSECTIONS.push({ x, y });
  });
});

export const PLACES = [
  { id: 'school', name: 'École Jean Jaurès', x: 100, y: 170, radius: 48, color: '#f59e0b', icon: '🏫', address: '12 Rue de l’École' },
  { id: 'home', name: 'Maison / Résidence', x: 900, y: 440, radius: 48, color: '#38bdf8', icon: '🏠', address: '44 Rue de Paris' },
  { id: 'park', name: 'Grand Parc des Lilas', x: 420, y: 260, radius: 52, color: '#10b981', icon: '🌳', address: 'Allée Centrale des Lilas' },
  { id: 'mall', name: 'Centre Commercial', x: 740, y: 170, radius: 50, color: '#ec4899', icon: '🛍️', address: '35 Rue de la République' },
  { id: 'police', name: 'Commissariat de Police', x: 580, y: 350, radius: 46, color: '#3b82f6', icon: '👮', address: '14 Bd Haussmann' },
  { id: 'metro', name: 'Station Métro Central', x: 740, y: 350, radius: 46, color: '#06b6d4', icon: '🚇', address: 'Place de la République' },
  { id: 'hospital', name: 'Hôpital Saint-Louis', x: 260, y: 80, radius: 48, color: '#ef4444', icon: '🏥', address: '8 Rue de Rivoli' },
  { id: 'cityhall', name: 'Mairie & Esplanade', x: 580, y: 170, radius: 48, color: '#eab308', icon: '🏛️', address: 'Esplanade Centrale' },
  { id: 'underground', name: 'Parking & Sous-Sol', x: 260, y: 440, radius: 48, color: '#a855f7', icon: '🅿️', address: 'Parking Souterrain' },
];

export const getStreetAddressForCoord = (x, y) => {
  for (const place of PLACES) {
    const dist = Math.hypot(x - place.x, y - place.y);
    if (dist <= 35) {
      return {
        isInPlace: true,
        placeName: place.name,
        address: place.address,
        distanceFromPlace: 0,
      };
    }
  }

  let closestStreet = STREET_SEGMENTS[0];
  let minD = 9999;
  STREET_SEGMENTS.forEach(s => {
    const d = s.orientation === 'H' ? Math.abs(y - s.y) : Math.abs(x - s.x);
    if (d < minD) {
      minD = d;
      closestStreet = s;
    }
  });

  const num = Math.max(2, Math.min(138, Math.floor((x + y) / 10) * 2));
  const school = PLACES[0];
  const distSchool = Math.round(Math.hypot(x - school.x, y - school.y) * 2.5);

  return {
    isInPlace: false,
    placeName: null,
    address: `${num} ${closestStreet.name}`,
    distanceFromPlace: distSchool,
  };
};

const City3DMap = ({
  simulationState,
  activePlaceId,
  onSelectPlace,
  childrenProfiles,
  parentProfile,
  onTriggerCrisis,
  onStopCrisis,
  onReset,
  incidentReport,
  onCloseReport,
  simulationConfig,
  setSimulationConfig,
  timeMultiplier,
  setTimeMultiplier,
  isGameStarted,
  onOpenSetupModal,
  selectedRole,
  userRPG,
  setUserRPG,
  attackerRPG,
  userXP = 0,
  userPV = 55,
  unlockedFeatures = [],
  onUnlockFeature,
  trustedContactsCount = 0,
  onAddTrustedContact,
  currentAgility,
  setCurrentAgility,
  currentCharisma,
  setCurrentCharisma,
  onSpendPV,
  onBuyPopulation,
  onBuyProtectors,
  onStartDirectly,
  onOpenWelcomeModal,
  duelProps,
  reportProps,
}) => {
  const canvasRef = useRef(null);
  const placesBarRef = useDragToScroll({ direction: 'horizontal', speed: 1.5 });
  const legendBarRef = useDragToScroll({ direction: 'horizontal', speed: 1.5 });
  const leftColRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const rightColRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  const [upgradeModal, setUpgradeModal] = useState(null); // 'population' | 'protectors' | null
  const [selectedTileDetail, setSelectedTileDetail] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState(null); // { message, success }

  const agility = userRPG?.agility !== undefined ? userRPG.agility : (currentAgility || 30);
  const composure = userRPG?.composure !== undefined ? userRPG.composure : (currentCharisma || 25);

  const v = simulationState.victim;
  const currentAddress = v ? getStreetAddressForCoord(v.x, v.y) : null;

  const totalCitizens = simulationState.citizens.length;
  const activeGuardians = simulationState.citizens.filter(c => c.hasApp).length;
  const whiteZoneCitizens = simulationState.citizens.filter(c => c.hasApp && c.isInPersonalWhiteZone).length;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const render = () => {
      const width = canvas.width;
      const height = canvas.height;

      // 1. Fond Urbain Profond
      ctx.fillStyle = simulationState.isNight ? '#050c18' : '#081427';
      ctx.fillRect(0, 0, width, height);

      // 2. Parcs & Espaces Verts
      ctx.fillStyle = 'rgba(16, 185, 129, 0.12)';
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.28)';
      ctx.lineWidth = 1.5;
      ctx.fillRect(340, 210, 160, 90);
      ctx.strokeRect(340, 210, 160, 90);
      ctx.fillStyle = 'rgba(16, 185, 129, 0.7)';
      ctx.font = '10px Plus Jakarta Sans, sans-serif';
      ctx.fillText('🌳 Grand Espace Vert des Lilas', 355, 255);

      ctx.fillStyle = 'rgba(16, 185, 129, 0.08)';
      ctx.fillRect(930, 70, 70, 70);
      ctx.strokeRect(930, 70, 70, 70);

      // 3. Rues bitumées et marquages
      STREET_SEGMENTS.forEach(street => {
        ctx.fillStyle = simulationState.isNight ? '#0f1b2d' : '#132238';

        if (street.orientation === 'H') {
          ctx.fillRect(street.xMin, street.y - 14, street.xMax - street.xMin, 28);
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.2)';
          ctx.lineWidth = 1;
          ctx.strokeRect(street.xMin, street.y - 14, street.xMax - street.xMin, 28);

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1.2;
          ctx.setLineDash([10, 8]);
          ctx.beginPath();
          ctx.moveTo(street.xMin, street.y);
          ctx.lineTo(street.xMax, street.y);
          ctx.stroke();
          ctx.setLineDash([]);
        } else {
          ctx.fillRect(street.x - 14, street.yMin, 28, street.yMax - street.yMin);
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.2)';
          ctx.lineWidth = 1;
          ctx.strokeRect(street.x - 14, street.yMin, 28, street.yMax - street.yMin);

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1.2;
          ctx.setLineDash([10, 8]);
          ctx.beginPath();
          ctx.moveTo(street.x, street.yMin);
          ctx.lineTo(street.x, street.yMax);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      });

      // 4. Carrefours Urbains
      INTERSECTIONS.forEach(inter => {
        ctx.fillStyle = simulationState.isNight ? '#16253c' : '#1a2c47';
        ctx.fillRect(inter.x - 14, inter.y - 14, 28, 28);
        ctx.strokeStyle = 'rgba(242, 184, 7, 0.25)';
        ctx.lineWidth = 1;
        ctx.strokeRect(inter.x - 14, inter.y - 14, 28, 28);
      });

      // 5. Noms des Rues
      ctx.fillStyle = 'rgba(203, 213, 225, 0.75)';
      ctx.font = 'bold 9px Plus Jakarta Sans, sans-serif';
      STREET_SEGMENTS.forEach(street => {
        if (street.orientation === 'H') {
          ctx.fillText(`🛣️ ${street.name}`, street.xMin + 10, street.y - 17);
        } else {
          ctx.save();
          ctx.translate(street.x - 18, street.yMin + 70);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(`🛣️ ${street.name}`, 0, 0);
          ctx.restore();
        }
      });

      // 6. Lieux 3D Isométriques
      PLACES.forEach(place => {
        const isMonitored = activePlaceId === place.id;

        const gradient = ctx.createRadialGradient(place.x, place.y, 4, place.x, place.y, place.radius);
        gradient.addColorStop(0, `${place.color}30`);
        gradient.addColorStop(0.7, `${place.color}12`);
        gradient.addColorStop(1, `${place.color}00`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(place.x, place.y, place.radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = isMonitored ? '#f2b807' : `${place.color}77`;
        ctx.lineWidth = isMonitored ? 2 : 1;
        ctx.setLineDash(isMonitored ? [4, 4] : []);
        ctx.beginPath();
        ctx.arc(place.x, place.y, place.radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        // Bâtiment 3D
        const bW = 46;
        const bH = 36;
        const depth = 11;
        const bx = place.x - bW / 2;
        const by = place.y - bH / 2;

        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(bx + depth, by + depth, bW, bH);

        ctx.fillStyle = 'rgba(15, 39, 74, 0.95)';
        ctx.beginPath();
        ctx.moveTo(bx + bW, by);
        ctx.lineTo(bx + bW + depth, by - depth);
        ctx.lineTo(bx + bW + depth, by + bH - depth);
        ctx.lineTo(bx + bW, by + bH);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = place.color;
        ctx.stroke();

        ctx.fillStyle = 'rgba(28, 62, 107, 0.95)';
        ctx.beginPath();
        ctx.moveTo(bx, by);
        ctx.lineTo(bx + depth, by - depth);
        ctx.lineTo(bx + bW + depth, by - depth);
        ctx.lineTo(bx + bW, by);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = place.color;
        ctx.stroke();

        ctx.fillStyle = 'rgba(11, 31, 58, 0.95)';
        ctx.fillRect(bx, by, bW, bH);
        ctx.strokeStyle = isMonitored ? '#f2b807' : 'rgba(255, 255, 255, 0.2)';
        ctx.strokeRect(bx, by, bW, bH);

        ctx.fillStyle = '#ffffff';
        ctx.font = '14px Plus Jakarta Sans, sans-serif';
        ctx.fillText(place.icon, place.x - 7, place.y + 4);

        ctx.font = 'bold 8.5px Outfit, sans-serif';
        ctx.fillStyle = isMonitored ? '#f2b807' : '#ffffff';
        ctx.fillText(place.name, place.x - bW / 2 - 4, place.y + bH / 2 + 13);
      });

      // 7. Citoyens (Points Verts, Blancs en Zone Blanche, Oranges notifiés, Dorés au secours)
      simulationState.citizens.forEach(c => {
        ctx.save();
        ctx.translate(c.x, c.y);
        const inWhiteZone = c.isInPersonalWhiteZone;

        if (c.hasApp) {
          if (c.isResponding) {
            const isAtContact = c.hasTouchedVictim || (simulationState.victim && Math.hypot(c.x - simulationState.victim.x, c.y - simulationState.victim.y) <= 14);

            ctx.fillStyle = isAtContact ? '#10b981' : '#f2b807';
            ctx.shadowColor = isAtContact ? '#10b981' : '#f2b807';
            ctx.shadowBlur = 12;
            ctx.beginPath();
            ctx.arc(0, 0, 5.5, 0, Math.PI * 2);
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 7.5px Outfit, sans-serif';
            ctx.fillText(isAtContact ? '🛡️ AU CONTACT' : '🏃 EN ROUTE', 7, -5);
          } else if (inWhiteZone) {
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
            ctx.shadowBlur = 8;
            ctx.beginPath();
            ctx.arc(0, 0, 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          } else if (c.hasReceivedAlert && !c.hasDeclined) {
            ctx.fillStyle = '#f97316';
            ctx.shadowColor = '#f97316';
            ctx.shadowBlur = 10;
            ctx.beginPath();
            ctx.arc(0, 0, 5, 0, Math.PI * 2);
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.fillStyle = '#ffedd5';
            ctx.font = 'bold 7px Outfit, sans-serif';
            ctx.fillText('📱 NOTIF', 6, -5);
          } else {
            ctx.fillStyle = '#10b981';
            ctx.shadowColor = 'rgba(16, 185, 129, 0.6)';
            ctx.shadowBlur = 5;
            ctx.beginPath();
            ctx.arc(0, 0, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          ctx.fillStyle = inWhiteZone ? '#ffffff' : '#64748b';
          ctx.beginPath();
          ctx.arc(0, 0, 3.2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();

        if (c.isResponding && simulationState.victim) {
          ctx.strokeStyle = 'rgba(242, 184, 7, 0.35)';
          ctx.setLineDash([3, 4]);
          ctx.beginPath();
          ctx.moveTo(c.x, c.y);
          ctx.lineTo(simulationState.victim.x, simulationState.victim.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      });

      // 8. Périmètre d'Alerte Géolocalisé (1/4 de la carte = 270px) & Ondes SOS
      if (simulationState.activeAlert && simulationState.victim) {
        const victimEntity = simulationState.victim;

        // Périmètre d'Alerte de Proximité (Rayon 270px = 1/4 carte)
        ctx.fillStyle = 'rgba(239, 68, 68, 0.05)';
        ctx.beginPath();
        ctx.arc(victimEntity.x, victimEntity.y, 270, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'rgba(239, 68, 68, 0.4)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.arc(victimEntity.x, victimEntity.y, 270, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = 'rgba(252, 165, 165, 0.85)';
        ctx.font = 'bold 8.5px Plus Jakarta Sans, sans-serif';
        ctx.fillText('📡 Périmètre de diffusion SOS (Rayon 1/4 de carte)', victimEntity.x - 105, victimEntity.y + 265);

        // Ondes d'alerte SOS
        const wave = (Date.now() / 18) % 270;
        ctx.strokeStyle = `rgba(239, 68, 68, ${Math.max(0, 0.8 - wave / 270)})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(victimEntity.x, victimEntity.y, wave, 0, Math.PI * 2);
        ctx.stroke();
      }

      // 9. Agresseur & Lutte au corps à corps
      if (simulationState.attacker && simulationState.attacker.active) {
        const att = simulationState.attacker;
        ctx.fillStyle = '#ef4444';
        ctx.shadowColor = '#ef4444';
        ctx.shadowBlur = att.isFleeing ? 14 : 10;
        ctx.beginPath();
        ctx.arc(att.x, att.y, 6.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        ctx.fillStyle = '#fca5a5';
        ctx.font = 'bold 8.5px Plus Jakarta Sans, sans-serif';
        ctx.fillText(
          att.isFleeing ? `🏃 En fuite !` : att.isConfronting ? `💥 Agression / Lutte !` : `⚠️ Agresseur`,
          att.x + 8,
          att.y - 6
        );
      }

      // 10. Enfant (si famille créée)
      if (childrenProfiles && childrenProfiles.length > 0 && simulationState.childEntity) {
        const ch = simulationState.childEntity;
        ctx.fillStyle = ch.isInPersonalWhiteZone ? '#ffffff' : '#38bdf8';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(ch.x, ch.y, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#bae6fd';
        ctx.font = 'bold 8px Plus Jakarta Sans, sans-serif';
        ctx.fillText(`👦 ${ch.name.split(' ')[0]}`, ch.x - 10, ch.y - 8);
      }

      // 11. Utilisateur Principal (Sarah)
      if (simulationState.victim) {
        const victimEntity = simulationState.victim;
        const isUnderAttack = simulationState.activeAlert || victimEntity.isConfronting;

        ctx.fillStyle = isUnderAttack ? '#ef4444' : '#f2b807';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = isUnderAttack ? 2.5 : 2;
        ctx.shadowColor = isUnderAttack ? '#ef4444' : 'rgba(242, 184, 7, 0.9)';
        ctx.shadowBlur = isUnderAttack ? 16 : 10;
        ctx.beginPath();
        ctx.arc(victimEntity.x, victimEntity.y, isUnderAttack ? 8 : 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;

        const tagWidth = isUnderAttack ? 68 : 40;
        ctx.fillStyle = isUnderAttack ? 'rgba(185, 28, 28, 0.95)' : 'rgba(5, 11, 20, 0.85)';
        ctx.strokeStyle = isUnderAttack ? '#fca5a5' : '#f2b807';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(victimEntity.x - tagWidth / 2, victimEntity.y - 20, tagWidth, 14, 4);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 8px Plus Jakarta Sans, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(
          isUnderAttack ? `🚨 ${parentProfile.name || 'Sarah'} (SOS)` : (parentProfile.name || 'Sarah'),
          victimEntity.x,
          victimEntity.y - 10
        );
        ctx.textAlign = 'left';
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => cancelAnimationFrame(animationFrameId);
  }, [simulationState, activePlaceId, childrenProfiles, parentProfile]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    for (const place of PLACES) {
      if (Math.hypot(clickX - place.x, clickY - place.y) <= place.radius) {
        onSelectPlace(place.id);
        return;
      }
    }
  };

  return (
    <div className="simulator-map-card" style={{
      flex: 1,
      minHeight: '480px',
      background: 'rgba(11, 31, 58, 0.9)',
      backdropFilter: 'blur(20px)',
      border: '1.5px solid rgba(242, 184, 7, 0.35)',
      borderRadius: '16px',
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @media (max-width: 900px) {
          .simulator-map-card {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            padding: 8px !important;
          }
        }
        .city-3d-main-grid {
          display: grid;
          grid-template-columns: 230px 1fr 240px;
          gap: 10px;
          flex: 1;
          min-height: 400px;
          align-items: stretch;
          position: relative;
        }
        .city-3d-left-col, .city-3d-right-col {
          overflow-y: auto !important;
          max-height: 100% !important;
          box-sizing: border-box !important;
        }
        .city-3d-places-bar {
          display: flex;
          gap: 3px;
          flex-wrap: wrap;
        }
        .city-3d-legend-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          font-size: 0.64rem;
          color: #cbd5e1;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          padding-top: 3px;
        }
        .mobile-controls-bar {
          display: none !important;
        }
        .desktop-controls-bar {
          display: flex !important;
          flex-direction: column !important;
          gap: 8px !important;
          border-top: 1.5px solid rgba(255, 255, 255, 0.1) !important;
          padding-top: 8px !important;
          margin-top: auto !important;
        }
        @media (max-width: 960px) {
          .mobile-controls-bar {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
            background: rgba(5, 11, 20, 0.85) !important;
            border: 1px solid rgba(255, 255, 255, 0.12) !important;
            border-radius: 12px !important;
            padding: 8px 12px !important;
            margin-top: 6px !important;
          }
          .desktop-controls-bar {
            display: none !important;
          }
          .city-3d-main-grid {
            display: flex !important;
            flex-direction: column !important;
            overflow-y: visible !important;
            gap: 12px !important;
          }
          .city-3d-canvas-box {
            order: 1 !important;
            min-height: 280px !important;
            height: auto !important;
            flex: none !important;
          }
          .city-3d-right-col {
            order: 2 !important;
          }
          .city-3d-left-col {
            order: 3 !important;
          }
          .city-3d-places-bar {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: 4px !important;
            width: 100% !important;
          }
          .city-3d-legend-bar {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: 2px !important;
            white-space: nowrap !important;
            font-size: 0.60rem !important;
          }
        }
      `}</style>

      {/* 1. TOP BAR : TITRE DE LA VILLE + SÉLECTEUR DES 9 LIEUX + LÉGENDE DE LA CARTE */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        background: 'rgba(5, 11, 20, 0.85)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        padding: '6px 10px',
      }}>
        {/* Ligne 1 : Titre + Lieux + Boutons Jeu/Musique */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '5px', background: 'rgba(242, 184, 7, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MapPin size={12} color="var(--mga-gold)" />
              </div>
              <h3 style={{ margin: 0, fontSize: '0.86rem', color: '#ffffff', fontWeight: 800, whiteSpace: 'nowrap' }}>
                🗺️ Grand Quartier Urbain MonAlerte
              </h3>
            </div>

            {/* Bouton Guide & Histoire RPG */}
            {onOpenWelcomeModal && (
              <button
                type="button"
                onClick={onOpenWelcomeModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  border: '1.2px solid var(--mga-gold)',
                  background: 'linear-gradient(135deg, rgba(242, 184, 7, 0.2) 0%, rgba(217, 119, 6, 0.3) 100%)',
                  color: 'var(--mga-gold)',
                  fontSize: '0.66rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(242, 184, 7, 0.25)',
                  whiteSpace: 'nowrap',
                }}
              >
                <Gamepad2 size={12} /> 🎮 Guide & Histoire
              </button>
            )}
          </div>

          {/* Sélecteur de Lieux */}
          <div ref={placesBarRef} className="city-3d-places-bar">
            {PLACES.map(p => (
              <button
                key={p.id}
                onClick={() => onSelectPlace(p.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '2px 5px',
                  borderRadius: '5px',
                  border: '1px solid ' + (activePlaceId === p.id ? 'var(--mga-gold)' : 'rgba(255, 255, 255, 0.1)'),
                  background: activePlaceId === p.id ? 'rgba(242, 184, 7, 0.2)' : 'rgba(15, 39, 74, 0.6)',
                  color: activePlaceId === p.id ? '#ffffff' : 'var(--mga-slate-300)',
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <span>{p.icon}</span> {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ligne 2 : Légende de la Carte */}
        <div ref={legendBarRef} className="city-3d-legend-bar">
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f2b807', display: 'inline-block', boxShadow: '0 0 4px #f2b807' }}></span> 
            <strong>{parentProfile.name || 'Sarah'} :</strong> Utilisateur
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span> 
            <strong>Vert :</strong> Protecteurs ({activeGuardians - whiteZoneCitizens})
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ffffff', display: 'inline-block', boxShadow: '0 0 4px #ffffff' }}></span> 
            <strong>Blanc :</strong> Zone Blanche ({whiteZoneCitizens})
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f97316', display: 'inline-block' }}></span> 
            <strong>Orange :</strong> Alerte reçue
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f2b807', display: 'inline-block', border: '1px solid #ffffff' }}></span> 
            <strong>Or :</strong> En route
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#64748b', display: 'inline-block' }}></span> 
            <strong>Gris :</strong> Passants ({totalCitizens - activeGuardians})
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}></span> 
            <strong>Rouge :</strong> Agresseur
          </span>
        </div>
      </div>

      {/* 2. ZONE CENTRALE EN 3 COLONNES : GAUCHE (XP + SOS + ENQUÊTE) | CENTRE (CARTE 3D) | DROITE (PV + STATS + CITOYENS/PROTECTEURS + VITESSE + DÉMARRER) */}
      <div className="city-3d-main-grid">
        {/* COLONNE GAUCHE : XP EN GROS + 🚨 DÉCLENCHEMENT SOS + 🔒 ENQUÊTE & SECOURS */}
        <div ref={leftColRef} className="city-3d-left-col" style={{
          background: 'linear-gradient(180deg, rgba(15, 39, 74, 0.75) 0%, rgba(5, 11, 20, 0.88) 100%)',
          border: '1.2px solid rgba(242, 184, 7, 0.35)',
          borderRadius: '12px',
          padding: '8px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          overflowY: 'auto',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
        }}>
          {/* XP EN GROS AU-DESSUS */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(242, 184, 7, 0.18) 0%, rgba(217, 119, 6, 0.3) 100%)',
            border: '1.5px solid var(--mga-gold)',
            borderRadius: '10px',
            padding: '6px 10px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(242, 184, 7, 0.25)',
          }}>
            <span style={{ fontSize: '0.62rem', color: '#ffd873', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ✨ EXPÉRIENCE DISPONIBLE
            </span>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--mga-gold)', textShadow: '0 2px 8px rgba(0,0,0,0.8)', lineHeight: 1.1, marginTop: '2px' }}>
              {userXP} XP
            </div>
          </div>

          {/* SECTION 2 : 🚨 DÉCLENCHEMENT SOS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '3px' }}>
              <div style={{ fontSize: '0.72rem', color: '#f87171', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Zap size={12} color="#f87171" /> 2. Déclenchement SOS
              </div>
              <span style={{ fontSize: '0.58rem', color: 'var(--mga-slate-400)' }}>Méthodes selon l'emplacement</span>
            </div>

            {/* Les 2 tuiles de déclenchement */}
            {['smartwatch', 'shortcutHardware'].map(key => {
              const meta = TILES_METADATA[key];
              const isUnlocked = unlockedFeatures.includes(key);
              const IconComp = key === 'smartwatch' ? Watch : Power;

              return (
                <div
                  key={key}
                  onClick={() => setSelectedTileDetail(meta)}
                  style={{
                    background: isUnlocked 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(5, 150, 105, 0.28) 100%)' 
                      : 'rgba(239, 68, 68, 0.06)',
                    border: isUnlocked ? '1.5px solid #10b981' : '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '7px',
                    padding: '5px 7px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title="Cliquez pour voir les forces, faiblesses et débloquer"
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '5px',
                    background: isUnlocked ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                    border: isUnlocked ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <IconComp size={13} color={isUnlocked ? '#4ade80' : '#f87171'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {meta ? meta.title.split(' (')[0] : key}
                    </div>
                    <span style={{ fontSize: '0.58rem', color: isUnlocked ? '#a7f3d0' : '#fca5a5', display: 'block', lineHeight: 1.1 }}>
                      {isUnlocked ? '🟢 Actif' : `${meta?.costXP || 60} XP`}
                    </span>
                  </div>
                  {!isUnlocked && (
                    <Lock size={10} color="#f87171" style={{ flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* SECTION 3 : 🔒 ENQUÊTE & SECOURS (EN DESSOUS DE DÉCLENCHEMENT SOS) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '2px' }}>
            <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '3px' }}>
              <div style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Users size={12} color="#38bdf8" /> 3. Enquête & Secours
              </div>
              <span style={{ fontSize: '0.58rem', color: 'var(--mga-slate-400)' }}>Mobilisation & preuves</span>
            </div>

            {/* Les 4 tuiles d'enquête */}
            {['guardians', 'contacts', 'audioLive', 'cameraCloud'].map(key => {
              const meta = TILES_METADATA[key];
              const isUnlocked = key === 'contacts' ? trustedContactsCount > 0 : unlockedFeatures.includes(key);
              const IconComp = (key === 'guardians' || key === 'contacts') ? Users : key === 'audioLive' ? Mic : Video;

              return (
                <div
                  key={key}
                  onClick={() => setSelectedTileDetail(meta)}
                  style={{
                    background: isUnlocked 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(5, 150, 105, 0.28) 100%)' 
                      : 'rgba(239, 68, 68, 0.06)',
                    border: isUnlocked ? '1.5px solid #10b981' : '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '7px',
                    padding: '5px 7px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title="Cliquez pour voir les forces, faiblesses et débloquer"
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '5px',
                    background: isUnlocked ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                    border: isUnlocked ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <IconComp size={13} color={isUnlocked ? '#4ade80' : '#f87171'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {meta ? meta.title.split(' (')[0] : key}
                    </div>
                    <span style={{ fontSize: '0.58rem', color: isUnlocked ? '#a7f3d0' : '#fca5a5', display: 'block', lineHeight: 1.1 }}>
                      {key === 'contacts' 
                        ? (trustedContactsCount > 0 ? `🟢 ${trustedContactsCount}/25 contact(s)` : `10 XP (Max 25)`)
                        : isUnlocked ? '🟢 Actif' : `${meta?.costXP || 60} XP`}
                    </span>
                  </div>
                  {key === 'contacts' ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (trustedContactsCount >= 25) {
                          setSelectedTileDetail(meta);
                        } else if (userXP >= 10 && onAddTrustedContact) {
                          onAddTrustedContact();
                        } else {
                          setSelectedTileDetail(meta);
                        }
                      }}
                      style={{
                        background: trustedContactsCount >= 25 
                          ? 'rgba(16, 185, 129, 0.25)' 
                          : userXP >= 10 
                          ? 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)' 
                          : 'rgba(255, 255, 255, 0.08)',
                        color: trustedContactsCount >= 25 ? '#86efac' : userXP >= 10 ? '#000000' : '#94a3b8',
                        border: '1px solid ' + (trustedContactsCount >= 25 ? '#10b981' : userXP >= 10 ? 'var(--mga-gold)' : 'rgba(255, 255, 255, 0.15)'),
                        padding: '3px 6px',
                        borderRadius: '5px',
                        fontSize: '0.60rem',
                        fontWeight: 900,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        boxShadow: userXP >= 10 && trustedContactsCount < 25 ? '0 2px 8px rgba(242, 184, 7, 0.3)' : 'none',
                      }}
                      title={trustedContactsCount >= 25 ? "Maximum de 25 contacts atteint" : "Ajouter directement +1 contact de confiance pour 10 XP"}
                    >
                      {trustedContactsCount >= 25 ? '✓ Max (25)' : '➕ +1 (10 XP)'}
                    </button>
                  ) : !isUnlocked && (
                    <Lock size={10} color="#f87171" style={{ flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* COLONNE CENTRALE : BANDEAU DUEL + CARTE 3D CANVAS */}
        <div className="city-3d-canvas-box" style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, position: 'relative' }}>
          {/* Bandeau Duel RPG si agression active */}
          {simulationState.attacker && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(15, 39, 74, 0.95) 0%, rgba(5, 11, 20, 0.98) 100%)',
              border: '1.5px solid rgba(239, 68, 68, 0.7)',
              borderRadius: '10px',
              padding: '4px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.85)',
              animation: 'fadeIn 0.2s ease',
            }}>
              {/* Sarah */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '1.2rem' }}>👩</span>
                <div>
                  <strong style={{ color: '#38bdf8', fontSize: '0.78rem', display: 'block' }}>
                    {userRPG?.name || 'Sarah'} (Victime)
                  </strong>
                  <span style={{ fontSize: '0.62rem', color: '#ffd873', fontWeight: 700 }}>
                    🚨 En danger face à l'agresseur
                  </span>
                </div>
              </div>

              {/* Compte à rebours */}
              <div style={{
                background: (simulationState.attacker.remainingSec !== undefined && simulationState.attacker.remainingSec <= 5) ? 'rgba(239, 68, 68, 0.35)' : 'rgba(245, 158, 11, 0.25)',
                border: '1px solid ' + ((simulationState.attacker.remainingSec !== undefined && simulationState.attacker.remainingSec <= 5) ? '#ef4444' : '#f59e0b'),
                borderRadius: '6px',
                padding: '2px 6px',
                fontSize: '0.65rem',
                fontWeight: 800,
                color: (simulationState.attacker.remainingSec !== undefined && simulationState.attacker.remainingSec <= 5) ? '#fca5a5' : '#ffd873',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <span>⏱️</span>
                <strong style={{ fontSize: '0.74rem' }}>{simulationState.attacker.remainingSec !== undefined ? simulationState.attacker.remainingSec : 25}s</strong>
              </div>

              {/* Agresseur */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'right' }}>
                <div>
                  <strong style={{ color: '#f87171', fontSize: '0.78rem', display: 'block' }}>
                    {simulationState.attacker.alias || attackerRPG?.alias || "L'Agresseur"}
                  </strong>
                  <span style={{ fontSize: '0.62rem', color: '#ffd873', fontWeight: 800 }}>
                    {Math.max(0, simulationState.attacker.charismaHp !== undefined ? simulationState.attacker.charismaHp : (attackerRPG?.charismaHp || 65))} / {simulationState.attacker.maxHp || attackerRPG?.maxHp || 65} PV
                  </span>
                </div>
                <span style={{ fontSize: '1.2rem' }}>🦹</span>
              </div>
            </div>
          )}

          {/* Canvas de la Carte 3D */}
          <div style={{ flex: 1, minHeight: '260px', height: '100%', borderRadius: '12px', overflow: 'hidden', border: '1.5px solid rgba(255, 255, 255, 0.12)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <canvas
              ref={canvasRef}
              width={1080}
              height={600}
              onClick={handleCanvasClick}
              style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer', objectFit: 'contain' }}
            />
          </div>

          {/* BARRE DE CONTRÔLES (VITESSE + DÉMARRER) DIRECTEMENT SOUS LA CARTE SUR MOBILE */}
          <div className="mobile-controls-bar">
            {/* Vitesse */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: '#cbd5e1', fontWeight: 800 }}>
                ⏱️ Vitesse :
              </span>
              <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '2px', border: '1px solid rgba(255, 255, 255, 0.15)', gap: '2px' }}>
                {[0.5, 1, 2, 4].map(spd => (
                  <button
                    key={spd}
                    onClick={() => setTimeMultiplier(spd)}
                    style={{
                      background: timeMultiplier === spd ? 'var(--mga-gold)' : 'transparent',
                      color: timeMultiplier === spd ? '#000000' : '#cbd5e1',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    x{spd}
                  </button>
                ))}
              </div>
            </div>

            {/* Bouton Démarrer / Arrêter / Reset */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {!isGameStarted ? (
                <button
                  onClick={() => {
                    if (onStartDirectly) onStartDirectly();
                    else if (onOpenSetupModal) onOpenSetupModal();
                  }}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    borderRadius: '9px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)',
                    color: '#000000',
                    fontWeight: 900,
                    fontSize: '0.90rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(242, 184, 7, 0.45)',
                    letterSpacing: '0.3px',
                  }}
                >
                  <Play size={16} fill="currentColor" /> 🎮 Démarrer
                </button>
              ) : !simulationState.activeAlert && !simulationState.attacker ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '9px 12px',
                  borderRadius: '9px',
                  background: 'rgba(56, 189, 248, 0.2)',
                  border: '1.5px solid #38bdf8',
                  color: '#bae6fd',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38bdf8', display: 'inline-block', boxShadow: '0 0 8px #38bdf8' }}></span>
                  En cours
                </div>
              ) : (
                <button
                  onClick={onStopCrisis}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '9px 12px',
                    borderRadius: '9px',
                    border: '1.5px solid #ef4444',
                    background: 'rgba(239, 68, 68, 0.25)',
                    color: '#fca5a5',
                    fontWeight: 800,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                  }}
                >
                  <Square size={13} fill="currentColor" /> ⏹️ Arrêter
                </button>
              )}

              <button
                onClick={onReset}
                title="Réinitialiser"
                style={{
                  padding: '9px 12px',
                  borderRadius: '9px',
                  border: '1.5px solid rgba(255, 255, 255, 0.18)',
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: 'var(--mga-slate-200)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* COLONNE DROITE : PV + CITIZENS/PROTECTEURS + VITESSE + DÉMARRER */}
        <div ref={rightColRef} className="city-3d-right-col" style={{
          background: 'linear-gradient(180deg, rgba(15, 39, 74, 0.75) 0%, rgba(5, 11, 20, 0.88) 100%)',
          border: '1.2px solid rgba(239, 68, 68, 0.35)',
          borderRadius: '12px',
          padding: '8px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          overflowY: 'auto',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* PV EN GROS AU-DESSUS (CLIQUABLE POUR EXPLICATION) */}
            <div 
              onClick={() => setUpgradeModal('pv_pool')}
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.18) 0%, rgba(185, 28, 28, 0.3) 100%)',
                border: '1.5px solid #ef4444',
                borderRadius: '10px',
                padding: '6px 10px',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Cliquez pour comprendre le fonctionnement des Points de Vitalité (PV)"
            >
              <span style={{ fontSize: '0.62rem', color: '#fca5a5', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                💖 VITALITÉ DISPONIBLE
              </span>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#f87171', textShadow: '0 2px 8px rgba(0,0,0,0.8)', lineHeight: 1.1, marginTop: '2px' }}>
                {userPV} PV
              </div>
            </div>



            {/* POPULATION TOTALE */}
            <div 
              onClick={() => setUpgradeModal('population')}
              style={{
                background: 'rgba(5, 11, 20, 0.7)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: '8px',
                padding: '6px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Cliquez pour comprendre l'impact de la Population"
            >
              <div>
                <div style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Users size={12} color="#38bdf8" /> 👥 Citoyens
                </div>
                <span style={{ fontSize: '0.60rem', color: '#38bdf8', fontWeight: 700 }}>
                  {totalCitizens} personnes
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (userPV >= 10 && simulationConfig.citizenCount < 200 && onBuyPopulation) {
                    onBuyPopulation();
                  } else {
                    setUpgradeModal('population');
                  }
                }}
                style={{
                  background: userPV >= 10 && simulationConfig.citizenCount < 200
                    ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' 
                    : 'rgba(255, 255, 255, 0.08)',
                  color: userPV >= 10 && simulationConfig.citizenCount < 200 ? '#ffffff' : '#94a3b8',
                  border: '1px solid ' + (userPV >= 10 ? '#38bdf8' : 'rgba(255, 255, 255, 0.15)'),
                  padding: '3px 7px',
                  borderRadius: '6px',
                  fontSize: '0.64rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                title="Ajouter directement +10 citoyens pour 10 PV (cliquez sur le texte pour voir les détails)"
              >
                ➕ +10 (10 PV)
              </button>
            </div>

            {/* POURCENTAGE DE PROTECTEURS */}
            <div 
              onClick={() => setUpgradeModal('protectors')}
              style={{
                background: 'rgba(5, 11, 20, 0.7)',
                border: '1px solid rgba(242, 184, 7, 0.25)',
                borderRadius: '8px',
                padding: '6px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Cliquez pour comprendre l'impact des Protecteurs"
            >
              <div>
                <div style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Shield size={12} color="var(--mga-gold)" /> 🛡️ Protecteurs
                </div>
                <span style={{ fontSize: '0.60rem', color: 'var(--mga-gold)', fontWeight: 700 }}>
                  {simulationConfig.appAdoptionRate}% ({activeGuardians})
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (userPV >= 10 && simulationConfig.appAdoptionRate < 90 && onBuyProtectors) {
                    onBuyProtectors();
                  } else {
                    setUpgradeModal('protectors');
                  }
                }}
                style={{
                  background: userPV >= 10 && simulationConfig.appAdoptionRate < 90
                    ? 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)' 
                    : 'rgba(255, 255, 255, 0.08)',
                  color: userPV >= 10 && simulationConfig.appAdoptionRate < 90 ? '#000000' : '#94a3b8',
                  border: '1px solid ' + (userPV >= 10 ? 'var(--mga-gold)' : 'rgba(255, 255, 255, 0.15)'),
                  padding: '3px 7px',
                  borderRadius: '6px',
                  fontSize: '0.64rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                title="Ajouter directement +10% protecteurs pour 10 PV (cliquez sur le texte pour voir les détails)"
              >
                ➕ +10% (10 PV)
              </button>
            </div>
          </div>

          {/* VITESSE & BOUTON DÉMARRER (AFFICHÉ DANS LA COLONNE DROITE SUR DESKTOP UNIQUEMENT) */}
          <div className="desktop-controls-bar">
            {/* Vitesse au-dessus du bouton démarrer */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: '#cbd5e1', fontWeight: 800 }}>
                ⏱️ Vitesse :
              </span>
              <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '2px', border: '1px solid rgba(255, 255, 255, 0.15)', gap: '2px' }}>
                {[0.5, 1, 2, 4].map(spd => (
                  <button
                    key={spd}
                    onClick={() => setTimeMultiplier(spd)}
                    style={{
                      background: timeMultiplier === spd ? 'var(--mga-gold)' : 'transparent',
                      color: timeMultiplier === spd ? '#000000' : '#cbd5e1',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    x{spd}
                  </button>
                ))}
              </div>
            </div>

            {/* Bouton Démarrer / Arrêter / Reset */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {!isGameStarted ? (
                <button
                  onClick={() => {
                    if (onStartDirectly) onStartDirectly();
                    else if (onOpenSetupModal) onOpenSetupModal();
                  }}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    borderRadius: '9px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)',
                    color: '#000000',
                    fontWeight: 900,
                    fontSize: '0.90rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(242, 184, 7, 0.45)',
                    letterSpacing: '0.3px',
                    transition: 'transform 0.1s ease',
                  }}
                >
                  <Play size={16} fill="currentColor" /> 🎮 Démarrer
                </button>
              ) : !simulationState.activeAlert && !simulationState.attacker ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '9px 12px',
                  borderRadius: '9px',
                  background: 'rgba(56, 189, 248, 0.2)',
                  border: '1.5px solid #38bdf8',
                  color: '#bae6fd',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38bdf8', display: 'inline-block', boxShadow: '0 0 8px #38bdf8' }}></span>
                  En cours
                </div>
              ) : (
                <button
                  onClick={onStopCrisis}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '9px 12px',
                    borderRadius: '9px',
                    border: '1.5px solid #ef4444',
                    background: 'rgba(239, 68, 68, 0.25)',
                    color: '#fca5a5',
                    fontWeight: 800,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                  }}
                >
                  <Square size={13} fill="currentColor" /> ⏹️ Arrêter
                </button>
              )}

              <button
                onClick={onReset}
                title="Réinitialiser"
                style={{
                  padding: '9px 12px',
                  borderRadius: '9px',
                  border: '1.5px solid rgba(255, 255, 255, 0.18)',
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: 'var(--mga-slate-200)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BLOCS D'ACTION & STATS DU DUEL (ARRIVÉE DIRECTE SUR L'INTERFACE PRINCIPALE SANS OVERLAY) */}
      {duelProps && duelProps.isOpen && (
        <TurnBasedDuelModal {...duelProps} trustedContactsCount={trustedContactsCount} />
      )}

      {/* RAPPORT DE FIN D'INCIDENT & ENQUÊTE (DIRECTEMENT SUR L'INTERFACE PRINCIPALE SANS OVERLAY) */}
      {reportProps && reportProps.isOpen && (
        <InvestigationReportModal {...reportProps} />
      )}

      {/* MODALE DE DÉTAIL & DÉBLOCAGE D'UNE TUILE (AU CLIC SUR LES TUILES DE GAUCHE / DROITE) */}
      <TileDetailModal
        isOpen={Boolean(selectedTileDetail)}
        onClose={() => setSelectedTileDetail(null)}
        tileInfo={selectedTileDetail}
        isUnlocked={
          selectedTileDetail?.id === 'contacts'
            ? trustedContactsCount > 0
            : unlockedFeatures.includes(selectedTileDetail?.id)
        }
        userXP={userXP}
        onUnlock={(featureId, costXP) => {
          if (onUnlockFeature) {
            const idToUnlock = typeof featureId === 'string' ? featureId : (featureId?.id || selectedTileDetail?.id);
            const xpCost = typeof costXP === 'number' ? costXP : (featureId?.costXP || selectedTileDetail?.costXP || 0);
            const ok = onUnlockFeature(idToUnlock, xpCost, 0);
            if (ok) setSelectedTileDetail(null);
          }
        }}
        unlockedFeatures={unlockedFeatures}
        trustedContactsCount={trustedContactsCount}
        onAddContact={() => {
          if (onAddTrustedContact) {
            const ok = onAddTrustedContact();
            if (ok) setSelectedTileDetail(null);
          }
        }}
      />

      {/* POPUP MODALE D'EXPLICATION ET VALIDATION DES ATTRIBUTS & UPGRADES PV */}
      {upgradeModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setUpgradeModal(null); }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(3, 7, 18, 0.45)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '16px',
            boxSizing: 'border-box',
            animation: 'fadeIn 0.15s ease',
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #0a172a 0%, #030a16 100%)',
            border: upgradeModal === 'pv_pool' 
              ? '2px solid #ef4444'
              : upgradeModal === 'agility' 
                ? '2px solid #38bdf8' 
                : upgradeModal === 'composure' 
                  ? '2px solid #4ade80' 
                  : upgradeModal === 'population' 
                    ? '2px solid #38bdf8' 
                    : '2px solid var(--mga-gold)',
            borderRadius: '22px',
            width: '100%',
            maxWidth: '560px',
            maxHeight: '88vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            padding: '22px 24px',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.95)',
            position: 'relative',
            color: '#ffffff',
          }}>
            {/* Bouton Fermer */}
            <button
              onClick={() => setUpgradeModal(null)}
              style={{
                position: 'absolute',
                top: '18px',
                right: '18px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#cbd5e1',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>

            {/* En-tête du Modal */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
              <div style={{
                width: '52px',
                height: '52px',
                borderRadius: '14px',
                background: upgradeModal === 'pv_pool'
                  ? 'rgba(239, 68, 68, 0.2)'
                  : upgradeModal === 'agility'
                    ? 'rgba(56, 189, 248, 0.2)'
                    : upgradeModal === 'composure'
                      ? 'rgba(74, 222, 128, 0.2)'
                      : upgradeModal === 'population'
                        ? 'rgba(56, 189, 248, 0.2)'
                        : 'rgba(242, 184, 7, 0.2)',
                border: '1.5px solid ' + (
                  upgradeModal === 'pv_pool' ? '#ef4444' :
                  upgradeModal === 'agility' ? '#38bdf8' :
                  upgradeModal === 'composure' ? '#4ade80' :
                  upgradeModal === 'population' ? '#38bdf8' : 'var(--mga-gold)'
                ),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {upgradeModal === 'pv_pool' && <Heart size={28} color="#f87171" />}
                {upgradeModal === 'agility' && <Zap size={28} color="#38bdf8" />}
                {upgradeModal === 'composure' && <Shield size={28} color="#4ade80" />}
                {upgradeModal === 'population' && <Users size={28} color="#38bdf8" />}
                {upgradeModal === 'protectors' && <Shield size={28} color="var(--mga-gold)" />}
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.2rem',
                  fontWeight: 900,
                  color: upgradeModal === 'pv_pool' ? '#f87171' :
                         upgradeModal === 'agility' ? '#38bdf8' :
                         upgradeModal === 'composure' ? '#4ade80' :
                         upgradeModal === 'population' ? '#38bdf8' : 'var(--mga-gold)'
                }}>
                  {upgradeModal === 'pv_pool' && '💖 Réserve de Vitalité (PV)'}
                  {upgradeModal === 'agility' && '⚡ Attribut : Agilité & Vitesse'}
                  {upgradeModal === 'composure' && '🛡️ Attribut : Charme & Sang-froid'}
                  {upgradeModal === 'population' && '👥 Renforcer la Population Urbaine'}
                  {upgradeModal === 'protectors' && '🛡️ Booster le Réseau de Protecteurs'}
                </h3>
                <span style={{ fontSize: '0.80rem', color: '#94a3b8' }}>
                  {upgradeModal === 'pv_pool' && 'Points de Vitalité d’action & d’amélioration RPG'}
                  {upgradeModal === 'agility' && `Niveau actuel : ${agility} / ${MAX_AGILITY_CAP} PV (Coût : 1 PV)`}
                  {upgradeModal === 'composure' && `Niveau actuel : ${composure} / ${MAX_CHARISMA_CAP} PV (Coût : 1 PV)`}
                  {upgradeModal === 'population' && '+10 Citoyens dans la ville (10 PV)'}
                  {upgradeModal === 'protectors' && '+10% d’adoption MonAlerte (10 PV)'}
                </span>
              </div>
            </div>

            {/* Explications Pédagogiques Complètes */}
            <div style={{ background: 'rgba(255, 255, 255, 0.04)', borderRadius: '16px', padding: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', marginBottom: '18px' }}>
              <div style={{ fontSize: '0.84rem', fontWeight: 800, color: '#f8fafc', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Info size={16} color={
                  upgradeModal === 'pv_pool' ? '#f87171' :
                  upgradeModal === 'agility' ? '#38bdf8' :
                  upgradeModal === 'composure' ? '#4ade80' :
                  upgradeModal === 'population' ? '#38bdf8' : 'var(--mga-gold)'
                } />
                <span>Rôle et utilité dans la simulation :</span>
              </div>

              {upgradeModal === 'pv_pool' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>💖</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Monnaie de vitalité disponible :</strong>
                      <span> Vos PV représentent votre réserve d'énergie vitale non allouée. Ils s'acquièrent lors de chaque agression ou incident réussi.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>⚡</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Améliorer vos Attributs (1 PV) :</strong>
                      <span> Vous pouvez dépenser 1 PV pour augmenter définitivement votre <strong>Agilité</strong> ou votre <strong>Charme / Sang-froid</strong>.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🏙️</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Investir dans la Ville (10 PV) :</strong>
                      <span> Vous pouvez investir 10 PV pour ajouter <strong>+10 Citoyens</strong> ou augmenter de <strong>+10% les Protecteurs</strong> équipés de l'application.</span>
                    </div>
                  </div>
                </div>
              )}

              {upgradeModal === 'agility' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🏃</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Rapidité de réaction :</strong>
                      <span> Détermine votre vitesse pour sortir le téléphone du sac (25%) ou de la poche (25%) et lancer une alerte avant d'être neutralisé.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🔘</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Exécution des gestes de secours :</strong>
                      <span> Réduit le coût en vitalité nécessaire pour actionner la Touche Raccourcis (5 appuis sur Power ou combinaison Volume).</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🔒</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Allocation définitive :</strong>
                      <span> Chaque +1 PV attribué augmente votre agilité maximale de façon irréversible.</span>
                    </div>
                  </div>
                </div>
              )}

              {upgradeModal === 'composure' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🧠</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Sang-froid sous le stress :</strong>
                      <span> Permet de ne pas céder à la panique face à l'intimidateur et de garder toute votre lucidité pour déclencher le SOS adapté.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🗣️</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Dissuasion de l'agresseur :</strong>
                      <span> Plus votre score de charme et d'assurance est élevé, plus vos actions défensives et verbales déstabilisent l'agresseur.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🔒</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Allocation définitive :</strong>
                      <span> Chaque +1 PV attribué augmente votre sang-froid maximal de façon irréversible.</span>
                    </div>
                  </div>
                </div>
              )}

              {upgradeModal === 'population' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🏙️</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Densité de population accrue :</strong>
                      <span> La ville passe de <strong>{simulationConfig.citizenCount}</strong> à <strong>{simulationConfig.citizenCount + 10} citoyens</strong>. Les rues, parcs et carrefours sont plus fréquentés.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>📍</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Plus de monde = Protecteurs plus proches :</strong>
                      <span> En augmentant la population globale, le nombre de personnes situées à moins de 270 mètres de Sarah augmente mécaniquement.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>👁️</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Dissuasion et témoins :</strong>
                      <span> Plus la foule est dense, plus l'agresseur est hésitant et plus les preuves/témoignages seront nombreux.</span>
                    </div>
                  </div>
                </div>
              )}

              {upgradeModal === 'protectors' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>⚡</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Taux d'Anges Gardiens augmenté :</strong>
                      <span> Le pourcentage de citoyens équipés de MonAlerte passe de <strong>{simulationConfig.appAdoptionRate}%</strong> à <strong>{Math.min(90, simulationConfig.appAdoptionRate + 10)}%</strong>.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>🏃</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Intervention instantanée & distance réduite :</strong>
                      <span> Plus il y a de Protecteurs dans la population, plus ils sont proches au moment du déclenchement du SOS et plus leur délai d'arrivée est rapide.</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span>👥</span>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Force du nombre face à l'agresseur :</strong>
                      <span> Davantage de Protecteurs reçoivent le signal simultanément et convergent ensemble pour faire fuir l'agresseur et interrompre l'agression.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Solde & Coût (affiché si pas pv_pool) */}
            {upgradeModal !== 'pv_pool' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                marginBottom: '18px',
              }}>
                <div>
                  <span style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'block' }}>Votre Solde PV Actuel :</span>
                  <strong style={{ fontSize: '1.05rem', color: '#f87171' }}>💖 {userPV} PV</strong>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'block' }}>Coût de l'Amélioration :</span>
                  <strong style={{
                    fontSize: '1.05rem',
                    color: (
                      (upgradeModal === 'agility' || upgradeModal === 'composure') ? (userPV >= 1 ? '#4ade80' : '#f87171') :
                      (userPV >= 10 ? '#4ade80' : '#f87171')
                    )
                  }}>
                    {(upgradeModal === 'agility' || upgradeModal === 'composure') ? '-1 PV' : '-10 PV'}
                  </strong>
                </div>
              </div>
            )}

            {/* Message de feedback temporaire si besoin */}
            {feedbackMsg && (
              <div style={{
                padding: '10px 14px',
                borderRadius: '10px',
                background: feedbackMsg.success ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: '1px solid ' + (feedbackMsg.success ? '#10b981' : '#ef4444'),
                color: feedbackMsg.success ? '#86efac' : '#fca5a5',
                fontSize: '0.80rem',
                fontWeight: 700,
                marginBottom: '14px',
                textAlign: 'center',
              }}>
                {feedbackMsg.message}
              </div>
            )}

            {/* Bouton de Confirmation d'Action */}
            {upgradeModal === 'pv_pool' ? (
              <button
                onClick={() => setUpgradeModal(null)}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '14px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                  color: '#ffffff',
                  fontWeight: 900,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 8px 25px rgba(239, 68, 68, 0.4)',
                }}
              >
                <Check size={18} strokeWidth={3} />
                <span>Compris !</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  const requiredPV = (upgradeModal === 'agility' || upgradeModal === 'composure') ? 1 : 10;
                  if (userPV < requiredPV) {
                    setFeedbackMsg({
                      success: false,
                      message: `❌ PV insuffisants (Requis : ${requiredPV} PV). Réussissez des alertes pour gagner des PV de vitalité !`
                    });
                    setTimeout(() => setFeedbackMsg(null), 3000);
                    return;
                  }

                  if (upgradeModal === 'agility') {
                    if (onSpendPV) {
                      onSpendPV('agility', 1);
                      setFeedbackMsg({ success: true, message: '⚡ +1 Agilité attribué avec succès (-1 PV) !' });
                      setTimeout(() => { setFeedbackMsg(null); setUpgradeModal(null); }, 1200);
                    }
                  } else if (upgradeModal === 'composure') {
                    if (onSpendPV) {
                      onSpendPV('composure', 1);
                      setFeedbackMsg({ success: true, message: '🛡️ +1 Charme & Sang-froid attribué avec succès (-1 PV) !' });
                      setTimeout(() => { setFeedbackMsg(null); setUpgradeModal(null); }, 1200);
                    }
                  } else if (upgradeModal === 'population') {
                    const success = onBuyPopulation && onBuyPopulation();
                    if (success) {
                      setFeedbackMsg({ success: true, message: '🎉 +10 Citoyens ajoutés avec succès (-10 PV) !' });
                      setTimeout(() => { setFeedbackMsg(null); setUpgradeModal(null); }, 1200);
                    }
                  } else if (upgradeModal === 'protectors') {
                    const success = onBuyProtectors && onBuyProtectors();
                    if (success) {
                      setFeedbackMsg({ success: true, message: '🎉 +10% de Protecteurs débloqués avec succès (-10 PV) !' });
                      setTimeout(() => { setFeedbackMsg(null); setUpgradeModal(null); }, 1200);
                    }
                  }
                }}
                disabled={
                  (upgradeModal === 'agility' && (userPV < 1 || agility >= MAX_AGILITY_CAP)) ||
                  (upgradeModal === 'composure' && (userPV < 1 || composure >= MAX_CHARISMA_CAP)) ||
                  (upgradeModal === 'population' && (userPV < 10 || simulationConfig.citizenCount >= 200)) ||
                  (upgradeModal === 'protectors' && (userPV < 10 || simulationConfig.appAdoptionRate >= 90))
                }
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '14px',
                  border: 'none',
                  background: (
                    (upgradeModal === 'agility' && userPV >= 1) ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' :
                    (upgradeModal === 'composure' && userPV >= 1) ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                    (upgradeModal === 'population' && userPV >= 10) ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' :
                    (upgradeModal === 'protectors' && userPV >= 10) ? 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)' :
                    'rgba(255, 255, 255, 0.1)'
                  ),
                  color: (
                    (upgradeModal === 'protectors' && userPV >= 10) ? '#000000' :
                    ((upgradeModal === 'agility' || upgradeModal === 'composure') && userPV >= 1) ? '#ffffff' :
                    (upgradeModal === 'population' && userPV >= 10) ? '#ffffff' : '#64748b'
                  ),
                  fontWeight: 900,
                  fontSize: '0.95rem',
                  cursor: (
                    ((upgradeModal === 'agility' || upgradeModal === 'composure') && userPV >= 1) ||
                    ((upgradeModal === 'population' || upgradeModal === 'protectors') && userPV >= 10)
                  ) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: (
                    ((upgradeModal === 'agility' || upgradeModal === 'population') && userPV >= 1) ? '0 8px 25px rgba(2, 132, 199, 0.4)' :
                    (upgradeModal === 'composure' && userPV >= 1) ? '0 8px 25px rgba(16, 185, 129, 0.4)' :
                    (upgradeModal === 'protectors' && userPV >= 10) ? '0 8px 25px rgba(242, 184, 7, 0.4)' : 'none'
                  ),
                }}
              >
                <Check size={18} strokeWidth={3} />
                <span>
                  {upgradeModal === 'agility' && (userPV >= 1 ? 'Valider l’attribution de +1 Agilité (1 PV)' : 'PV insuffisants pour +1 Agilité (Requis : 1 PV)')}
                  {upgradeModal === 'composure' && (userPV >= 1 ? 'Valider l’attribution de +1 Charme (1 PV)' : 'PV insuffisants pour +1 Charme (Requis : 1 PV)')}
                  {upgradeModal === 'population' && (userPV >= 10 ? 'Valider l’ajout de +10 Citoyens (10 PV)' : 'PV insuffisants pour +10 Citoyens (Requis : 10 PV)')}
                  {upgradeModal === 'protectors' && (userPV >= 10 ? 'Valider l’augmentation de +10% (10 PV)' : 'PV insuffisants pour +10% (Requis : 10 PV)')}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default City3DMap;
